import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  VStack,
  HStack,
  Tag,
  Button,
  useColorModeValue,
  Link,
  Divider,
  SimpleGrid,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaGithub, FaArrowLeft, FaDownload } from 'react-icons/fa';
import DownloadModal from './DownloadModal';

const ProjectDetailLayout = ({ project }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Handle download function - now opens modal
  const handleDownload = () => {
    onOpen();
  };

  // Handle coming soon projects
  if (project.comingSoon) {
    return (
      <Container maxW="container.xl" py={8} mt="120px">
        <Button 
          as={RouterLink} 
          to="/projects" 
          leftIcon={<FaArrowLeft />} 
          colorScheme="blue" 
          variant="ghost" 
          mb={8}
        >
          Back to Projects
        </Button>
        
        <VStack spacing={8} align="center" py={16}>
          <Badge colorScheme="purple" fontSize="lg" px={4} py={2} borderRadius="md">
            Coming Soon
          </Badge>
          <Heading as="h1" size="2xl" textAlign="center" color={headingColor}>
            {project.title}
          </Heading>
          <Text fontSize="xl" textAlign="center" maxW="600px">
            {project.description}
          </Text>
          <Text fontSize="md" textAlign="center" maxW="500px" color="gray.500">
            This project is currently under development. Stay tuned for updates!
          </Text>
          
          <HStack spacing={2} flexWrap="wrap" justify="center">
            {project.tags.map((tag) => (
              <Tag key={tag} colorScheme="blue" size="md">
                {tag}
              </Tag>
            ))}
          </HStack>
        </VStack>
      </Container>
    );
  }

  return (
    <>
      <Container maxW="container.xl" py={8} mt="120px">
        <Button 
          as={RouterLink} 
          to="/projects" 
          leftIcon={<FaArrowLeft />} 
          colorScheme="blue" 
          variant="ghost" 
          mb={8}
        >
          Back to Projects
        </Button>
        
        <Box
          borderRadius="lg"
          overflow="hidden"
          mb={8}
          height={{ base: "200px", md: "400px" }}
          position="relative"
        >
          <Image 
            src={project.image} 
            alt={project.title}
            objectFit="cover"
            width="100%"
            height="100%"
          />
        </Box>
        
        <VStack spacing={8} align="start">
          <Box>
            <Heading as="h1" size="2xl" mb={4} color={headingColor}>
              {project.title}
            </Heading>
            
            <HStack spacing={2} mb={6} flexWrap="wrap">
              {project.tags.map((tag) => (
                <Tag key={tag} colorScheme="blue" size="md" my={1}>
                  {tag}
                </Tag>
              ))}
            </HStack>
            
            <Text fontSize="xl" fontWeight="medium" mb={4}>
              {project.description}
            </Text>
            
            {project.longDescription.split('\n\n').map((paragraph, index) => (
              <Text key={index} mb={4}>
                {paragraph}
              </Text>
            ))}
          </Box>
          
          {/* Action Buttons */}
          <HStack spacing={4} flexWrap="wrap">
            {project.github && (
              <Button 
                as={Link} 
                href={project.github} 
                isExternal
                leftIcon={<FaGithub />} 
                colorScheme="blue"
              >
                GitHub
              </Button>
            )}
            
            {project.downloadLink && (
              <Button 
                onClick={handleDownload}
                leftIcon={<FaDownload />} 
                colorScheme="green"
                // size="lg"
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                transition="all 0.2s"
              >
                Download Application
              </Button>
            )}
          </HStack>

          {/* Key Highlights Section */}
          {project.keyHighlights && (
            <>
              <Divider my={6} />
              <Box>
                <Heading as="h3" size="lg" mb={4} color={headingColor}>
                  Key Highlights
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {project.keyHighlights.map((highlight, index) => (
                    <HStack key={index} align="start">
                      <Box as="span" color="green.500" fontWeight="bold" mr={2} mt={1}>
                        ✓
                      </Box>
                      <Text>{highlight}</Text>
                    </HStack>
                  ))}
                </SimpleGrid>
              </Box>
            </>
          )}

          {/* Project Details Section */}
          {(project.projectType || project.status || project.compatibility) && (
            <>
              <Divider my={6} />
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                {project.projectType && (
                  <Box>
                    <Heading as="h4" size="md" mb={2} color="blue.500">
                      Project Type
                    </Heading>
                    <Text>{project.projectType}</Text>
                  </Box>
                )}
                
                {project.status && (
                  <Box>
                    <Heading as="h4" size="md" mb={2} color="blue.500">
                      Status
                    </Heading>
                    <Badge colorScheme="green" px={2} py={1}>
                      {project.status}
                    </Badge>
                  </Box>
                )}
                
                {project.compatibility && (
                  <Box>
                    <Heading as="h4" size="md" mb={2} color="blue.500">
                      Compatibility
                    </Heading>
                    <HStack spacing={2} flexWrap="wrap">
                      {project.compatibility.map((platform) => (
                        <Tag key={platform} colorScheme="blue" size="md" fontSize="sm">
                          {platform}
                        </Tag>
                      ))}
                    </HStack>
                  </Box>
                )}
              </SimpleGrid>
            </>
          )}
          
          <Divider my={4} />
          
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} width="100%">
            <Box>
              <Heading as="h3" size="lg" mb={4} color={headingColor}>
                Key Features
              </Heading>
              
              <VStack align="start" spacing={2}>
                {project.features.map((feature, index) => (
                  <HStack key={index} align="start">
                    <Box as="span" color="blue.500" fontWeight="bold" mr={2}>
                      •
                    </Box>
                    <Text>{feature}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
            
            <Box>
              <Heading as="h3" size="lg" mb={4} color={headingColor}>
                Technologies Used
              </Heading>
              
              <HStack spacing={2} flexWrap="wrap">
                {project.technologies.map((tech) => (
                  <Tag key={tech} colorScheme="purple" size="md" my={1}>
                    {tech}
                  </Tag>
                ))}
              </HStack>
            </Box>
          </SimpleGrid>
        </VStack>
      </Container>

      {/* Download Modal */}
      <DownloadModal
        isOpen={isOpen}
        onClose={onClose}
        productName={project.productName || project.title}
        productVersion={project.productVersion}
        displayName={project.title}
        downloadUrl={project.downloadLink}
        downloadFileName={project.downloadFileName}        
      />
    </>
  );
};

export default ProjectDetailLayout;







